import { useCallback, useState } from 'react';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import * as Sentry from '@sentry/capacitor';

export const PASSENGER_NOTIFICATION_ASKED_ON_HOME_KEY =
	'passenger_notificationAskedOnHome';

interface INotificationPermissionHook {
	showPermissionModal: boolean;
	checkAndRequestPermission: (context?: 'home' | 'action') => Promise<void>;
	closePermissionModal: () => void;
	hasAskedPermission: boolean;
}

/**
 * Hook to manage notification permissions
 * - Only asks if permission is NOT granted yet
 * - If granted: never asks again
 * - Home page: asks once per login session (persisted across app reopens)
 * - Actions (DTicket/Booking): always ask if not granted (not persisted)
 * - After logout → login: asks again if still not granted
 */
export const useNotificationPermission = (): INotificationPermissionHook => {
	const [showPermissionModal, setShowPermissionModal] = useState(false);
	const [hasAskedPermission, setHasAskedPermission] = useState(false);

	const checkAndRequestPermission = useCallback(
		async (context: 'home' | 'action' = 'action') => {
			try {
				// Don't ask again if already asked in this session
				if (hasAskedPermission) {
					return;
				}

				// For home page: check if we already asked in this login session
				if (context === 'home') {
					const hasAskedOnHome = localStorage.getItem(
						PASSENGER_NOTIFICATION_ASKED_ON_HOME_KEY
					);
					if (hasAskedOnHome === 'true') {
						return;
					}
				}

				const permissions = await FirebaseMessaging.checkPermissions();

				// Only show modal if permission is not granted
				// If granted, never ask again
				if (permissions.receive !== 'granted') {
					setShowPermissionModal(true);
					setHasAskedPermission(true);

					// Mark as asked on home page (persisted across app reopens)
					if (context === 'home') {
						localStorage.setItem(
							PASSENGER_NOTIFICATION_ASKED_ON_HOME_KEY,
							'true'
						);
					}
				}
			} catch (err) {
				Sentry.captureException(err);
			}
		},
		[hasAskedPermission]
	);

	const closePermissionModal = useCallback(() => {
		setShowPermissionModal(false);
	}, []);

	return {
		showPermissionModal,
		checkAndRequestPermission,
		closePermissionModal,
		hasAskedPermission,
	};
};
